import React, { useState, useEffect, useCallback } from 'react';
import Wrapper from '../../components/Wrapper';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Popper,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import KeyIcon from '@mui/icons-material/Key';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useNavigate, useParams } from 'react-router-dom';
import { openAi, shortFunction, input, aiAnalytics } from '../../translate';
import Api from '../../Api';
import { greenTheme, inputStyles, inputLabelStyles, sectionTitleStyles, buttonStyles } from '../../helpers/style';
import { toast } from 'react-toastify';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

const ZONE_NOT_SELECTED = 'zone-not-selected';
const ZONE_SELECTED = 'zone-selected';
const AI_ANALYTICS_DOMAIN = 'https://callcenter-backend-31345432073.europe-west1.run.app/api/external/audio-upload';

function SipCard({ sipItem }) {
  const id = sipItem.buddy || String(sipItem.id);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { sipItem },
  });
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.75,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: isDragging ? 'grabbing' : 'grab',
        bgcolor: isDragging ? 'action.hover' : 'transparent',
        opacity: isDragging ? 0.35 : 1,
        transition: 'opacity 0.2s',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {sipItem.buddy || sipItem.id} — {sipItem.notes || sipItem.email || ''}
        </Typography>
        {sipItem.provider ? (
          <Typography variant="caption" color="text.secondary">
            {sipItem.provider}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function matchSipItem(item, q) {
  if (!q || !String(q).trim()) return true;
  const s = String(q).toLowerCase().trim();
  const fields = [item.buddy, item.id, item.notes, item.email, item.provider]
    .filter(Boolean)
    .map((x) => String(x).toLowerCase());
  return fields.some((f) => f.includes(s));
}

function DropBlock({
  id,
  title,
  count,
  children,
  isEmpty,
  searchValue,
  onSearchChange,
  searchPlaceholder,
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: 1,
        minWidth: 0,
        border: '2px solid',
        borderColor: isOver ? '#7ebc0a' : 'divider',
        borderRadius: 2,
        bgcolor: isOver ? 'rgba(126,188,10,0.06)' : 'grey.50',
        transition: 'border-color 0.2s, background-color 0.2s',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          py: 1,
          px: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
          {title} ({count})
        </Typography>
        {onSearchChange != null && (
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: greenTheme.primary },
                '&:hover fieldset': { borderColor: greenTheme.hover },
                '&.Mui-focused fieldset': { borderColor: greenTheme.primary },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: greenTheme.primary },
            }}
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: greenTheme.primary }} />
                </InputAdornment>
              ),
              endAdornment: (searchValue ?? '') ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange('')} edge="end" aria-label="clear">
                    <ClearIcon fontSize="small" sx={{ color: greenTheme.primary }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        )}
      </Box>
      <Box sx={{ flex: 1, minHeight: 120, maxHeight: '40vh', overflowY: 'auto', p: 0.5 }}>
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            —
          </Typography>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}

export default function AiAnalytics() {
  const langIndex = +localStorage.getItem('atsLang') || 1;
  const navigate = useNavigate();
  const { analyticsId } = useParams();
  const analyticsItem = aiAnalytics.find((item) => item.id === decodeURIComponent(analyticsId || '')) || null;
  const langSuffix = langIndex === 3 ? 'hy' : langIndex === 2 ? 'ru' : 'en';
  const pageTitle = analyticsItem
    ? (analyticsItem[`title_${langSuffix}`] || analyticsItem.title_en || openAi.ai_analytics_title?.[langIndex])
    : (openAi.ai_analytics_title?.[langIndex] || 'AI Analytics');
  const pageDescription = analyticsItem
    ? (analyticsItem[`long_description_${langSuffix}`] || analyticsItem.long_description_en || openAi.ai_analytics_desc[langIndex])
    : openAi.ai_analytics_desc[langIndex];

  const [key, setKey] = useState('');
  const [showOperators, setShowOperators] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const [sipList, setSipList] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [loadingSips, setLoadingSips] = useState(false);
  const [savingOperators, setSavingOperators] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [searchNotSelected, setSearchNotSelected] = useState('');
  const [searchSelected, setSearchSelected] = useState('');

  const activeItem = activeId && sipList.find((item) => (item.buddy || String(item.id)) === activeId);
  const [helpAnchor, setHelpAnchor] = useState(null);
  const helpCloseTimeoutRef = React.useRef(null);

  const handleHelpOpen = (e) => {
    if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current);
    setHelpAnchor(e.currentTarget);
  };
  const handleHelpClose = () => {
    helpCloseTimeoutRef.current = setTimeout(() => setHelpAnchor(null), 150);
  };
  const handleHelpCloseClear = () => {
    if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current);
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const fetchConfig = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const { data } = await Api.aiAnalyticsConfigGet();
      const cfg = data?.data ?? data;
      const k = cfg?.key ?? '';
      setKey(k);
      if (k) setShowOperators(true);
    } catch (e) {
      setKey('');
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const fetchSips = useCallback(async () => {
    try {
      setLoadingSips(true);
      const [listRes, operatorsRes] = await Promise.all([
        Api.shortSipList(),
        Api.aiAnalyticsOperatorsGet(),
      ]);
      const list = Array.isArray(listRes?.data?.data) ? listRes.data.data : [];
      setSipList(list);
      const raw = operatorsRes?.data?.buddies ?? operatorsRes?.data ?? [];
      const buddies = Array.isArray(raw) ? raw : [];
      setSelectedOperators(buddies);
    } catch (e) {
      setSipList([]);
      setSelectedOperators([]);
    } finally {
      setLoadingSips(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (showOperators) fetchSips();
  }, [showOperators, fetchSips]);

  const handleConfigSave = useCallback(() => {
    const k = key.trim();
    if (!k) {
      toast.error('Please fill Key');
      return;
    }
    setShowOperators(true);
  }, [key]);


  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const buddy = active.id;
    if (over.id === ZONE_SELECTED) {
      setSelectedOperators((prev) => (prev.includes(buddy) ? prev : [...prev, buddy]));
    } else if (over.id === ZONE_NOT_SELECTED) {
      setSelectedOperators((prev) => prev.filter((b) => b !== buddy));
    }
  }, []);

  const handleSaveOperators = useCallback(async () => {
    try {
      setSavingOperators(true);
      await Api.aiAnalyticsOperatorsSave({
        buddies: selectedOperators,
        key: key.trim(),
        domain: AI_ANALYTICS_DOMAIN,
      });
      toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSavingOperators(false);
    }
  }, [selectedOperators, key, langIndex]);

  const handleCancel = useCallback(() => {
    navigate('/short_function/ai_analytics');
  }, [navigate]);

  const notSelectedItems = sipList.filter((item) => !selectedOperators.includes(item.buddy || item.id));
  const selectedItems = sipList.filter((item) => selectedOperators.includes(item.buddy || item.id));
  const filteredNotSelected = notSelectedItems.filter((item) => matchSipItem(item, searchNotSelected));
  const filteredSelected = selectedItems.filter((item) => matchSipItem(item, searchSelected));


  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '2px solid rgba(0, 0, 0, 0.08)',
              pb: 2.5,
            }}
          >
            <IconButton
              size="small"
              sx={{ color: '#7ebc0a', fontSize: 18 }}
              onClick={() => navigate('/short_function/ai_analytics')}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {pageTitle}
            </Typography>
            <IconButton
              size="small"
              sx={{ color: '#7ebc0a' }}
              onMouseEnter={handleHelpOpen}
              onMouseLeave={handleHelpClose}
              aria-label={shortFunction.speech_help_tooltip?.[langIndex] || 'How it works'}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          <Popper open={Boolean(helpAnchor)} anchorEl={helpAnchor} placement="bottom-start" modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}>
            <Paper elevation={8} onMouseEnter={handleHelpCloseClear} onMouseLeave={handleHelpClose} sx={{ mt: 0, overflow: 'visible', width: 400 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  {shortFunction.speech_help_title?.[langIndex] || 'How to use'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {pageDescription}
                </Typography>
              </Box>
            </Paper>
          </Popper>

          <Alert icon={<InfoIcon />} severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">{pageDescription}</Typography>
          </Alert>

          {/* Key & Domain */}
          <Paper sx={{ p: 3, border: `1px solid ${greenTheme.primary}` }}>
            {loadingConfig ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} sx={{ color: '#7ebc0a' }} />
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={sectionTitleStyles}>
                    <KeyIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 22, color: greenTheme.primary }} />
                    {openAi.ai_analytics_key[langIndex]}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label={openAi.ai_analytics_key[langIndex]}
                    placeholder={openAi.ai_analytics_key_placeholder[langIndex]}
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    variant="outlined"
                    sx={inputStyles}
                    InputLabelProps={{ sx: inputLabelStyles }}
                  />
                </Box>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleConfigSave}
                    sx={{
                      ...buttonStyles,
                      backgroundColor: '#7ebc0a !important',
                      color: '#fff !important',
                      '&:hover': { backgroundColor: '#6aa507 !important', color: '#fff !important' },
                    }}
                  >
                    {openAi.ai_analytics_save_config[langIndex]}
                  </Button>
                </Box>
              </>
            )}
          </Paper>

          {/* Operators — when key & domain filled */}
          {showOperators && (
            <>
              {loadingSips ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#7ebc0a' }} />
                </Box>
              ) : !sipList.length ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                  {shortFunction.operator_empty?.[langIndex]}
                </Typography>
              ) : (
                <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <DropBlock
                      id={ZONE_NOT_SELECTED}
                      title={openAi.ai_analytics_not_selected[langIndex]}
                      count={notSelectedItems.length}
                      isEmpty={!filteredNotSelected.length}
                      searchValue={searchNotSelected}
                      onSearchChange={setSearchNotSelected}
                      searchPlaceholder={input.search[langIndex]}
                    >
                      {filteredNotSelected.map((sipItem) => (
                        <SipCard key={sipItem.buddy || sipItem.id} sipItem={sipItem} />
                      ))}
                    </DropBlock>
                    <DropBlock
                      id={ZONE_SELECTED}
                      title={openAi.ai_analytics_selected[langIndex]}
                      count={selectedItems.length}
                      isEmpty={!filteredSelected.length}
                      searchValue={searchSelected}
                      onSearchChange={setSearchSelected}
                      searchPlaceholder={input.search[langIndex]}
                    >
                      {filteredSelected.map((sipItem) => (
                        <SipCard key={sipItem.buddy || sipItem.id} sipItem={sipItem} />
                      ))}
                    </DropBlock>
                  </Box>

                  <DragOverlay
                    dropAnimation={{
                      duration: 200,
                      easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                    }}
                  >
                    {activeItem ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0.75,
                          px: 1.5,
                          borderRadius: 1,
                          boxShadow: 3,
                          bgcolor: 'background.paper',
                          border: '2px solid',
                          borderColor: '#7ebc0a',
                          cursor: 'grabbing',
                          minWidth: 200,
                        }}
                      >
                        <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {activeItem.buddy || activeItem.id} — {activeItem.notes || activeItem.email || ''}
                          </Typography>
                          {activeItem.provider ? (
                            <Typography variant="caption" color="text.secondary">
                              {activeItem.provider}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    color: '#7ebc0a',
                    borderColor: '#7ebc0a',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#7ebc0a', backgroundColor: 'rgba(126,188,10,0.08)' },
                  }}
                >
                  {shortFunction.operator_close?.[langIndex]}
                </Button>
                <Button
                  variant="contained"
                  disabled={savingOperators || !key.trim()}
                  onClick={handleSaveOperators}
                  sx={{
                    backgroundColor: '#7ebc0a',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#6aa507' },
                  }}
                >
                  {savingOperators ? '...' : shortFunction.operator_save?.[langIndex]}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </div>
    </Wrapper>
  );
}
